import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, Baby, BookOpen, GraduationCap, TrendingUp, Loader2, CheckCircle, Clock } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format, startOfWeek, endOfWeek, eachDayOfInterval } from "date-fns";
import { ar } from "date-fns/locale";
import HelpTooltip from "@/components/HelpTooltip";

const CHART_GREEN = "hsl(160, 55%, 38%)";
const CHART_BLUE = "hsl(205, 75%, 48%)";
const PIE_COLORS = [CHART_GREEN, CHART_BLUE, "hsl(160, 55%, 55%)", "hsl(205, 75%, 65%)", "hsl(40, 80%, 55%)", "hsl(340, 60%, 55%)"];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border rounded-lg p-3 shadow-elevated text-sm">
      <p className="font-medium text-card-foreground mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} style={{ color: entry.color }} className="text-xs">
          {entry.name}: {entry.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
};

const statHelp: Record<string, string> = {
  "إجمالي الأطفال": "العدد الكلي للأطفال المسجلين في الحضانة حالياً",
  "الموظفين": "عدد الموظفين والمعلمين العاملين في الحضانة",
  "الفصول النشطة": "عدد الفصول الدراسية المفعّلة في الحضانة",
  "الامتحانات": "إجمالي عدد الامتحانات التي تم إنشاؤها",
};

const Dashboard = () => {
  const { profile, roles } = useAuth();
  const isParent = roles.includes("parent") && !roles.includes("admin") && !roles.includes("teacher");

  const nurseryId = profile?.nursery_id;
  const today = format(new Date(), "yyyy-MM-dd");
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const weekEnd = endOfWeek(new Date(), { weekStartsOn: 0 });

  const { data: childrenCount = 0, isLoading: l1 } = useQuery({
    queryKey: ["dash-children", nurseryId],
    queryFn: async () => {
      const { count } = await supabase.from("children").select("*", { count: "exact", head: true }).eq("nursery_id", nurseryId!);
      return count || 0;
    },
    enabled: !!nurseryId,
  });

  const { data: staffCount = 0, isLoading: l2 } = useQuery({
    queryKey: ["dash-staff", nurseryId],
    queryFn: async () => {
      const { count } = await supabase.from("staff").select("*", { count: "exact", head: true }).eq("nursery_id", nurseryId!);
      return count || 0;
    },
    enabled: !!nurseryId,
  });

  const { data: classesCount = 0, isLoading: l3 } = useQuery({
    queryKey: ["dash-classes", nurseryId],
    queryFn: async () => {
      const { count } = await supabase.from("classes").select("*", { count: "exact", head: true }).eq("nursery_id", nurseryId!);
      return count || 0;
    },
    enabled: !!nurseryId,
  });

  const { data: examsCount = 0 } = useQuery({
    queryKey: ["dash-exams", nurseryId],
    queryFn: async () => {
      const { count } = await supabase.from("exams").select("*", { count: "exact", head: true }).eq("nursery_id", nurseryId!);
      return count || 0;
    },
    enabled: !!nurseryId,
  });

  // Today's attendance
  const { data: todayAttendance } = useQuery({
    queryKey: ["dash-today-attendance", nurseryId, today],
    queryFn: async () => {
      const { data } = await supabase.from("attendance").select("attendee_type, status").eq("nursery_id", nurseryId!).eq("date", today);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const todayChildPresent = (todayAttendance || []).filter((a) => a.attendee_type === "child" && (a.status === "present" || a.status === "late")).length;
  const todayStaffPresent = (todayAttendance || []).filter((a) => a.attendee_type === "staff" && (a.status === "present" || a.status === "late")).length;

  // Weekly attendance
  const { data: weekAttendance } = useQuery({
    queryKey: ["dash-week-attendance", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("attendance").select("date, attendee_type, status")
        .eq("nursery_id", nurseryId!)
        .gte("date", format(weekStart, "yyyy-MM-dd"))
        .lte("date", format(weekEnd, "yyyy-MM-dd"));
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const weeklyChartData = useMemo(() => {
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    return days.map((day) => {
      const dayStr = format(day, "yyyy-MM-dd");
      const dayRecords = (weekAttendance || []).filter((r) => r.date === dayStr);
      return {
        day: format(day, "EEEE", { locale: ar }),
        أطفال: dayRecords.filter((r) => r.attendee_type === "child" && (r.status === "present" || r.status === "late")).length,
        موظفين: dayRecords.filter((r) => r.attendee_type === "staff" && (r.status === "present" || r.status === "late")).length,
      };
    });
  }, [weekAttendance]);

  // Children per class for pie chart
  const { data: classDistribution } = useQuery({
    queryKey: ["dash-class-dist", nurseryId],
    queryFn: async () => {
      const { data: classes } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!);
      const { data: children } = await supabase.from("children").select("class_id").eq("nursery_id", nurseryId!);
      if (!classes) return [];
      const counts: Record<string, number> = {};
      (children || []).forEach((c) => { if (c.class_id) counts[c.class_id] = (counts[c.class_id] || 0) + 1; });
      return classes.map((cls) => ({ name: cls.name, value: counts[cls.id] || 0 })).filter((c) => c.value > 0);
    },
    enabled: !!nurseryId,
  });

  const isLoading = l1 || l2 || l3;

  const statCards = [
    { title: "إجمالي الأطفال", value: childrenCount, icon: Baby },
    { title: "الموظفين", value: staffCount, icon: Users },
    { title: "الفصول النشطة", value: classesCount, icon: BookOpen },
    { title: "الامتحانات", value: examsCount, icon: GraduationCap },
  ];

  const todayRate = childrenCount > 0 ? Math.round((todayChildPresent / childrenCount) * 100) : 0;

  if (isParent) return <Navigate to="/dashboard/parent" replace />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">لوحة التحكم</h1>
        <p className="text-muted-foreground text-sm mt-1">نظرة عامة على حضانتك</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
            <Card className="shadow-card hover:shadow-elevated transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg gradient-hero flex items-center justify-center">
                    <stat.icon className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <HelpTooltip text={statHelp[stat.title] || stat.title} side="left" iconSize={13} />
                </div>
                <div className="text-2xl font-bold text-card-foreground">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : stat.value}
                </div>
                <div className="text-sm text-muted-foreground mt-1">{stat.title}</div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="shadow-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              الحضور الأسبوعي
              <HelpTooltip text="رسم بياني يوضح عدد الأطفال والموظفين الحاضرين خلال أيام الأسبوع الحالي" side="bottom" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={weeklyChartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="day" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="أطفال" fill={CHART_GREEN} radius={[4, 4, 0, 0]} />
                <Bar dataKey="موظفين" fill={CHART_BLUE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              توزيع الطلبة على الفصول
              <HelpTooltip text="رسم دائري يوضح نسبة توزيع الأطفال على الفصول المختلفة" side="bottom" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(classDistribution || []).length === 0 ? (
              <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">لا توجد بيانات</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={classDistribution} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                      {(classDistribution || []).map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {(classDistribution || []).map((cls, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span>{cls.name} ({cls.value})</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Today's Attendance */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              الحضور اليوم
              <HelpTooltip text="يعرض عدد الأطفال والموظفين الحاضرين اليوم مقارنة بالعدد الإجمالي" side="bottom" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {[
              { label: "الأطفال الحاضرين", value: todayChildPresent, total: childrenCount },
              { label: "الموظفين الحاضرين", value: todayStaffPresent, total: staffCount },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-card-foreground">{item.label}</span>
                  <span className="text-muted-foreground">{item.value}/{item.total}</span>
                </div>
                <div className="h-3 rounded-full bg-muted overflow-hidden">
                  <motion.div
                    className="h-full rounded-full gradient-hero"
                    initial={{ width: 0 }}
                    animate={{ width: `${item.total > 0 ? (item.value / item.total) * 100 : 0}%` }}
                    transition={{ duration: 0.8, delay: 0.3 + i * 0.2 }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              ملخص سريع
              <HelpTooltip text="ملخص سريع لأهم الأرقام والإحصائيات في الحضانة" side="bottom" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "نسبة حضور اليوم", value: `${todayRate}%` },
                { label: "عدد الفصول", value: classesCount },
                { label: "عدد الأطفال", value: childrenCount },
                { label: "عدد الموظفين", value: staffCount },
              ].map((s, i) => (
                <div key={i} className="p-4 rounded-lg bg-muted/50 text-center">
                  <div className="text-xl font-bold text-foreground">{isLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
